/**
 * header.js — header shell components. Loaded from sections/header.liquid and
 * sections/announcement-bar.liquid as a module; browsers dedupe it.
 *
 *   <sticky-header data-sticky="none|always|scroll-up">
 *     Sets --header-height on :root, handles scroll-up hiding and the transparent-over-hero state,
 *     opens the menu drawer and search dialog (focus trapped via theme.js), and keeps the cart count
 *     in step with theme:cart:update. Cart button emits theme:cart:open when a drawer is present.
 *   <header-menu>
 *     Desktop disclosures (<details>): hover open on pointer devices, one open at a time,
 *     Escape closes and returns focus, close on focus leaving, theme editor block selection.
 *   <announcement-bar data-mode="rotate" data-interval="5">
 *     Rotation with prev/next/pause; no autoplay under prefers-reduced-motion or when hidden.
 *
 * Everything works without this file: <details> menus, a plain search link, a cart link.
 */
import { trapFocus, emit, EVENTS, define } from './theme.js';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const HOVER = window.matchMedia('(hover: hover)');

function openDialog(dialog, trigger) {
  if (!dialog || dialog.open) return;
  dialog.showModal();
  trigger?.setAttribute('aria-expanded', 'true');
  const release = trapFocus(dialog, { onEscape: () => dialog.close() });
  const onBackdrop = (event) => { if (event.target === dialog) dialog.close(); };
  dialog.addEventListener('click', onBackdrop);
  dialog.addEventListener('close', () => {
    dialog.removeEventListener('click', onBackdrop);
    trigger?.setAttribute('aria-expanded', 'false');
    release();
  }, { once: true });
}

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.sticky || 'none';
    this.lastY = window.scrollY;
    this.ticking = false;

    this.setHeight();
    this.resizeObserver = new ResizeObserver(() => this.setHeight());
    this.resizeObserver.observe(this);

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
    // The drawer's own search link lives outside this element.
    document.addEventListener('click', this.onDocumentClick = (event) => {
      const searchToggle = event.target.closest('[data-search-toggle]');
      if (searchToggle && !this.contains(searchToggle)) this.openSearch(event, searchToggle);
      const closer = event.target.closest('[data-menu-close], [data-search-close]');
      if (closer) closer.closest('dialog')?.close();
    });

    this.onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener(EVENTS.cartUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener(EVENTS.cartUpdate, this.onCartUpdate);
  }

  setHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      if (this.mode === 'scroll-up') {
        const height = this.offsetHeight;
        const panelOpen = this.querySelector('details[open]');
        if (y > this.lastY && y > height * 2 && !panelOpen) this.classList.add('is-hidden');
        else if (y < this.lastY - 2 || y <= height) this.classList.remove('is-hidden');
      }
      this.lastY = y;
      this.ticking = false;
    });
  }

  onClick(event) {
    const menuToggle = event.target.closest('[data-menu-toggle]');
    if (menuToggle) {
      event.preventDefault();
      openDialog(document.getElementById(menuToggle.getAttribute('aria-controls')), menuToggle);
      return;
    }
    const searchToggle = event.target.closest('[data-search-toggle]');
    if (searchToggle) {
      this.openSearch(event, searchToggle);
      return;
    }
    const cart = event.target.closest('[data-cart-toggle]');
    if (cart && cart.dataset.cartType === 'drawer' && document.querySelector('cart-drawer, #shopify-section-cart-drawer')) {
      event.preventDefault();
      emit(EVENTS.cartOpen, { source: 'header' });
    }
  }

  openSearch(event, toggle) {
    const dialog = document.getElementById(toggle.getAttribute('aria-controls') || '') || document.querySelector('.search-dialog');
    if (!dialog) return;
    event.preventDefault();
    const openDrawer = toggle.closest('dialog[open]');
    if (openDrawer) openDrawer.close();
    const headerToggle = this.querySelector('[data-search-toggle]');
    openDialog(dialog, headerToggle);
    dialog.querySelector('input[type="search"]')?.focus();
  }

  onCartUpdate(event) {
    if (event.detail?.cart?.item_count == null) return;
    const count = Number(event.detail.cart.item_count);
    const bubble = this.querySelector('[data-cart-count]');
    const label = this.querySelector('[data-cart-count-label]');
    const link = this.querySelector('[data-cart-toggle]');
    if (bubble) {
      bubble.textContent = String(count);
      bubble.hidden = count === 0;
    }
    if (label && link) {
      const template = count === 1 ? link.dataset.countOne : link.dataset.countOther;
      label.textContent = (template || '[count]').replace('[count]', String(count));
    }
  }
}

class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-menu-disclosure]'));
    this.timers = new WeakMap();

    this.items.forEach((details) => {
      const summary = details.querySelector('summary');
      const item = details.closest('li') || details;

      details.addEventListener('toggle', () => {
        summary?.setAttribute('aria-expanded', String(details.open));
        if (details.open) this.closeOthers(details);
      });

      if (HOVER.matches) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.timers.get(details));
          details.open = true;
        });
        item.addEventListener('mouseleave', () => {
          this.timers.set(details, setTimeout(() => { details.open = false; }, 150));
        });
      }

      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;
        event.preventDefault();
        event.stopPropagation();
        details.open = false;
        summary?.focus();
      });

      details.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !details.contains(event.relatedTarget)) details.open = false;
      });
    });

    this.onBlockSelect = (event) => {
      const details = event.target.closest?.('[data-menu-disclosure]') || this.querySelector(`[data-menu-disclosure]:has(#${CSS.escape(event.target.id || '')})`);
      const owner = event.target.closest ? event.target.closest('details') : null;
      if (owner && this.contains(owner)) owner.open = true;
      else if (details) details.open = true;
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect = (event) => {
      const owner = event.target.closest?.('details');
      if (owner && this.contains(owner)) owner.open = false;
    });
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  closeOthers(current) {
    this.items.forEach((details) => { if (details !== current && details.open) details.open = false; });
  }
}

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mode !== 'rotate') return;
    this.items = Array.from(this.querySelectorAll('[data-announcement]'));
    if (this.items.length < 2) return;
    this.index = Math.max(0, this.items.findIndex((el) => !el.hidden));
    this.interval = Math.max(3, Number(this.dataset.interval) || 5) * 1000;
    this.pauseButton = this.querySelector('[data-announcement-pause]');
    this.pauseLabel = this.querySelector('[data-announcement-pause-label]');
    this.autoplay = !REDUCED.matches;

    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.pauseButton?.addEventListener('click', () => {
      this.autoplay = !this.autoplay;
      this.autoplay ? this.start() : this.stop();
    });
    this.addEventListener('mouseenter', () => this.stop(false));
    this.addEventListener('mouseleave', () => { if (this.autoplay) this.start(); });
    this.addEventListener('focusin', () => this.stop(false));
    this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget) && this.autoplay) this.start(); });
    this.onVisibility = () => { document.hidden ? this.stop(false) : (this.autoplay && this.start()); };
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onBlockSelect = (event) => {
      const i = this.items.indexOf(event.target);
      if (i >= 0) { this.autoplay = false; this.stop(); this.show(i); }
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.show(this.index);
    this.autoplay ? this.start() : this.stop();
  }

  disconnectedCallback() {
    this.stop(false);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  show(i, restart = false) {
    const n = this.items.length;
    this.index = ((i % n) + n) % n;
    this.items.forEach((el, j) => { el.hidden = j !== this.index; });
    if (restart && this.autoplay) this.start();
  }

  start() {
    clearInterval(this.timer);
    this.timer = setInterval(() => this.show(this.index + 1), this.interval);
    this.setPressed(false);
  }

  /** stop(true) reflects a user pause in the button; stop(false) is a temporary pause. */
  stop(reflect = true) {
    clearInterval(this.timer);
    this.timer = null;
    if (reflect) this.setPressed(!this.autoplay);
  }

  setPressed(paused) {
    if (!this.pauseButton) return;
    this.pauseButton.setAttribute('aria-pressed', String(paused));
    if (this.pauseLabel) this.pauseLabel.textContent = paused ? this.pauseLabel.dataset.labelPlay : this.pauseLabel.dataset.labelPause;
  }
}

define('sticky-header', StickyHeader);
define('header-menu', HeaderMenu);
define('announcement-bar', AnnouncementBar);
